import { Component, computed, input, output, signal, Type } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { KeyFilter, KeyFilterPattern } from 'primeng/keyfilter';
import { Tooltip } from 'primeng/tooltip';
import { ErrorRequiredComponent } from '../errors/error-required/error-required';
import { MaxLengthComponent } from '../errors/max-length/max-length';
import { ErrorMinComponent } from '../errors/error-min/error-min';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../button/button';
import { DynamicIconComponent } from '../dynamic-icon/dynamic-icon';
import { fieldControlId } from '../field/field-control-id';
import { Eye } from '../../icons/eye/eye';
import { EyeOff } from '../../icons/eye-off/eye-off';

@Component({
  standalone: true,
  selector: 'app-text-input',
  imports: [
    ReactiveFormsModule,
    IconField,
    InputIcon,
    InputText,
    InputNumber,
    InputGroup,
    InputGroupAddon,
    KeyFilter,
    Tooltip,
    ErrorRequiredComponent,
    MaxLengthComponent,
    ErrorMinComponent,
    ButtonComponent,
    DynamicIconComponent,
    Eye,
    EyeOff,
  ],
  templateUrl: './text-input.html',
  styleUrl: './text-input.scss',
})
export class TextInput {
  hasIcon = input(false);
  isNumberInput = input(false);

  formCtrl = input.required<FormControl>();
  ctrlPlaceholder = input('');
  ctrlLabel = input('');
  formCtrlId = input<string>();
  formCtrlType = input('text');
  formCtrlClass = input('');
  tooltip = input<string | undefined>();
  isDisabled = input(false);
  ctrlKeyFilter = input<RegExp | KeyFilterPattern | null | undefined>();
  ctrlUseGrouping = input(true);
  shouldFocus = input(false);
  btnLabel = input<string | undefined>();
  btnIcon = input<Type<any> | undefined>();
  currency = input<string>('');
  hideErrors = input(false);
  shortcutHint = input<string>('');

  protected readonly inputId = fieldControlId(this.formCtrlId);

  /** Password fields get a show/hide toggle; the input type follows it. */
  protected readonly isPassword = computed(() => this.formCtrlType() === 'password');
  protected readonly passwordVisible = signal(false);
  protected readonly inputType = computed(() =>
    this.isPassword() && this.passwordVisible() ? 'text' : this.formCtrlType()
  );

  protected togglePasswordVisible() {
    this.passwordVisible.update((v) => !v);
  }

  keyReleased = output<boolean>();
  inputLeft = output<string | number>();
  onEnterClicked = output<string>();
  btnClicked = output<MouseEvent>();

  handleKeyUp(value: KeyboardEvent) {
    this.keyReleased.emit(true);
    if (value.key === 'Enter') {
      this.onEnterClicked.emit('');
    }
  }

  handleBlur(event: FocusEvent) {
    const target = event.target as HTMLInputElement;
    this.inputLeft.emit(target.value);
  }

  handleBtnClick(event: MouseEvent) {
    this.btnClicked.emit(event);
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonSeverity = ButtonSeverity;
}
